import { footballApiGet, FootballApiError } from "@/services/football/FootballApiClient";
import { cacheGet, cacheSet, CACHE_TTL, ttlForMatches } from "@/services/football/footballCache";
import { mapCompetition, mapMatch, mapTeamToClub, stageLabel } from "@/services/football/FootballApiMapper";
import type {
  RawCompetitionsResponse,
  RawMatchesResponse,
  RawMatch,
  RawTeamsResponse,
} from "@/services/football/footballApiTypes";
import type { Competition } from "@/modules/football/types/football";
import type { Club } from "@/modules/leagues/types/admin";
import type { Match } from "@/modules/leagues/types/match";

/**
 * FootballApiService (item 3 do escopo) — único ponto do domínio que sabe
 * buscar competições/clubes/partidas reais. Hooks e repositories dependem
 * só deste serviço (nunca de FootballApiClient diretamente), e ele nunca
 * expõe tipos brutos — sempre Competition/Club/Match normalizados.
 */
export class FootballDataService {
  async getCompetitions(): Promise<Competition[]> {
    const cacheKey = "competitions";
    const cached = cacheGet<Competition[]>(cacheKey);
    if (cached) return cached;

    const raw = await footballApiGet<RawCompetitionsResponse>("/competitions");
    const competitions = raw.competitions.map(mapCompetition);
    cacheSet(cacheKey, competitions, CACHE_TTL.competitions);
    return competitions;
  }

  async getClubs(competitionId: string): Promise<Club[]> {
    const cacheKey = `clubs:${competitionId}`;
    const cached = cacheGet<Club[]>(cacheKey);
    if (cached) return cached;

    const raw = await footballApiGet<RawTeamsResponse>(`/competitions/${competitionId}/teams`);
    const clubs = raw.teams.map(mapTeamToClub);
    cacheSet(cacheKey, clubs, CACHE_TTL.clubs);
    return clubs;
  }

  /**
   * Partidas de uma competição, filtradas por temporada e opcionalmente por
   * rodada (`matchday`) e fase (`stage`). `season` é OBRIGATÓRIO na prática —
   * sem ela a API usa a temporada "atual" dela, que pode não bater com a
   * temporada salva na Liga. `leagueId` só é usado para carimbar o Match
   * normalizado — a API não sabe o que é uma "Liga" do Palpiteiro.
   *
   * `stage`, quando informado, filtra no CLIENTE (a API não documenta um
   * parâmetro de query pra isso) — necessário porque `matchday` sozinho é
   * AMBÍGUO em competições com fases (o número reinicia a cada fase nova:
   * existe "rodada 1" tanto na fase de grupos quanto nos playoffs da
   * Libertadores, por exemplo). Sem esse filtro, pedir "rodada 1" numa
   * competição assim misturaria jogos de fases completamente diferentes.
   */
  async getMatches(competitionId: string, leagueId: string, season: number, matchday?: number, stage?: string | null): Promise<Match[]> {
    const cacheKey = `matches:${competitionId}:${season}:${matchday ?? "current"}:${stage ?? "any"}`;
    const cached = cacheGet<Match[]>(cacheKey);
    if (cached) return cached;

    const raw = await footballApiGet<RawMatchesResponse>(`/competitions/${competitionId}/matches`, {
      season,
      matchday,
    });
    const filteredRaw = stage ? raw.matches.filter((m) => m.stage === stage) : raw.matches;
    const matches = filteredRaw.map((m) => mapMatch(m, leagueId));
    const hasLive = matches.some((m) => m.status === "live");
    // TTL dinâmico: se algum jogo do lote já começou (por horário) ou está
    // prestes a começar, o cache tem que ser curto — senão a tela mostra
    // "Agendada" e libera o campo de palpite bem depois do jogo já ter
    // começado de verdade (bug relatado: Grêmio x São Paulo 16h).
    const ttl = ttlForMatches(
      matches.map((m) => m.kickoff),
      hasLive
    );
    cacheSet(cacheKey, matches, ttl);
    return matches;
  }

  async getMatch(matchId: string, leagueId: string): Promise<Match | null> {
    // ids normalizados vêm como "fd-<id-numérico-da-api>" (ver FootballApiMapper.mapMatch)
    const numericId = matchId.startsWith("fd-") ? matchId.slice(3) : matchId;
    const cacheKey = `match:${numericId}`;
    const cached = cacheGet<Match | null>(cacheKey);
    if (cached !== undefined) return cached;

    try {
      const raw = await footballApiGet<RawMatch>(`/matches/${numericId}`);
      const match = mapMatch(raw, leagueId);
      // Reaproveita a MESMA função robusta já usada pra lista de partidas
      // (ttlForMatches) — considera o horário real, não só o status atual.
      // Bug real corrigido: antes, só dava cache curto se o status JÁ
      // fosse "live" — só que enquanto a API demora a atualizar pra
      // "live" (mesma limitação de sempre), essa partida ficava presa em
      // cache de ATÉ 1 HORA com o status antigo ("scheduled"), mesmo já
      // tendo começado de verdade. Isso explicava tudo: mesmo com a tela
      // já sabendo checar o horário certo, o OBJETO da partida que ela
      // recebia continuava sendo o antigo, porque o cache nem tentava
      // buscar de novo.
      cacheSet(cacheKey, match, ttlForMatches([match.kickoff], match.status === "live"));
      return match;
    } catch (err) {
      if (err instanceof FootballApiError && err.code === "not_found") {
        cacheSet(cacheKey, null, CACHE_TTL.matchesUpcoming);
        return null;
      }
      throw err;
    }
  }

  /**
   * Últimos resultados (forma recente) de um time — endpoint documentado
   * `/teams/{id}/matches?status=FINISHED&limit=N` da football-data.org.
   * Cache de algumas horas: a forma de um time só muda quando ele joga de
   * novo, não precisa ser tão curto quanto o cache de partida ao vivo.
   */
  async getTeamRecentForm(teamId: string, limit = 5): Promise<Array<{ matchId: string; result: "V" | "E" | "D" }>> {
    const cacheKey = `team-form:${teamId}:${limit}`;
    const cached = cacheGet<Array<{ matchId: string; result: "V" | "E" | "D" }>>(cacheKey);
    if (cached) return cached;

    try {
      const raw = await footballApiGet<RawMatchesResponse>(`/teams/${teamId}/matches`, {
        status: "FINISHED",
        limit,
      });
      const form = raw.matches
        .slice()
        .sort((a, b) => new Date(b.utcDate).getTime() - new Date(a.utcDate).getTime())
        .map((m): { matchId: string; result: "V" | "E" | "D" } => {
          const isHome = String(m.homeTeam.id) === teamId;
          const winner = m.score.winner;
          let result: "V" | "E" | "D" = "E";
          if (winner === "DRAW" || winner === null) result = "E";
          else if ((winner === "HOME_TEAM" && isHome) || (winner === "AWAY_TEAM" && !isHome)) result = "V";
          else result = "D";
          return { matchId: `fd-${m.id}`, result };
        });
      cacheSet(cacheKey, form, CACHE_TTL.clubs); // 12h — forma recente não muda entre partidas
      return form;
    } catch (err) {
      // Forma recente é só um complemento visual — se falhar, a tela de
      // detalhes segue funcionando sem essa seção (nunca quebra o essencial).
      if (err instanceof FootballApiError) return [];
      throw err;
    }
  }

  /**
   * Rodada (e fase, se houver) que deve ser mostrada por padrão quando o
   * usuário não escolhe uma explicitamente.
   *
   * NUNCA confia cegamente no campo `currentSeason.currentMatchday` da
   * API — ele fica desatualizado assim que a competição muda de fase
   * (bug real confirmado com a Libertadores: esse campo ficou preso na
   * "rodada 6", a última da fase de grupos, já encerrada há meses,
   * enquanto os playoffs — com jogos de hoje/amanhã — já iam pela rodada
   * 1 de uma fase nova; a API não atualiza esse campo pra refletir isso).
   *
   * Em vez disso, busca a temporada inteira (1 chamada, já cacheada) e
   * acha a partida mais próxima de "agora": a próxima que ainda não
   * começou, ou a última que já aconteceu se não houver nenhuma futura —
   * mesma heurística já usada em FirestoreCachedMatchRepository
   * (inferCurrentRound) pra API-Football, agora também aqui.
   */
  async getCurrentRoundInfo(competitionId: string, season: number): Promise<{ round: number; stage: string | null } | null> {
    const cacheKey = `current-round:${competitionId}:${season}`;
    const cached = cacheGet<{ round: number; stage: string | null } | null>(cacheKey);
    if (cached !== undefined) return cached;

    const raw = await footballApiGet<RawMatchesResponse>(`/competitions/${competitionId}/matches`, { season });
    const withMatchday = raw.matches.filter((m): m is RawMatch & { matchday: number } => typeof m.matchday === "number");

    if (withMatchday.length === 0) {
      cacheSet(cacheKey, null, CACHE_TTL.matchesUpcoming);
      return null;
    }

    const now = Date.now();
    const withStatus = withMatchday.map((m) => ({
      round: m.matchday,
      stage: m.stage ?? null,
      time: new Date(m.utcDate).getTime(),
      status: m.status,
    }));

    // Prioridade 1: alguma partida ROLANDO AGORA (IN_PLAY/PAUSED) — isso é
    // mais "atual" que qualquer coisa futura, mesmo que outra partida da
    // mesma competição esteja marcada pra daqui a alguns dias. Bug real
    // relatado: Fluminense jogando ao vivo, mas o app mostrava a rodada de
    // outro confronto ainda nem começado, só porque ele vinha "antes" na
    // ordem cronológica de próximos jogos — a lógica nunca checava se
    // alguma partida já estava em andamento.
    const live = withStatus.find((m) => m.status === "IN_PLAY" || m.status === "PAUSED");

    let chosen: { round: number; stage: string | null };
    if (live) {
      chosen = live;
    } else {
      const byTime = [...withStatus].sort((a, b) => a.time - b.time);
      const upcoming = byTime.find((m) => m.time > now);
      chosen = upcoming ?? byTime[byTime.length - 1]!; // nenhuma futura = pega a última já jogada
    }

    const info = { round: chosen.round, stage: chosen.stage };
    const anyLive = Boolean(live);
    cacheSet(cacheKey, info, ttlForMatches(withMatchday.map((m) => m.utcDate), anyLive));
    return info;
  }

  /**
   * Ano que a football-data.org considera "temporada atual" PRA ESSA
   * competição específica (derivado de `currentSeason.startDate`).
   *
   * IMPORTANTE — motivo de existir: a Liga guarda um `season` fixo definido
   * na criação (calendário civil, ex: 2026). Isso bate certo pra
   * competições de temporada = ano civil (Brasileirão). NÃO bate pra
   * competições europeias (Champions League, etc.), cuja temporada na API
   * é rotulada pelo ANO DE INÍCIO — ex: temporada 2025/26 é `season=2025`,
   * não 2026. Se a Liga foi criada com `season=2026` apontando pra uma
   * Champions League, a API não retorna nenhuma partida (o "jogos não
   * aparecem" relatado) — não porque os dados não existem, mas porque
   * perguntamos pela temporada errada. Ver uso em FootballDataMatchRepository.
   */
  async getApiCurrentSeasonYear(competitionId: string): Promise<number | null> {
    const cacheKey = `season-year:${competitionId}`;
    const cached = cacheGet<number | null>(cacheKey);
    if (cached !== undefined) return cached;

    const raw = await footballApiGet<RawCompetitionsResponse>("/competitions");
    const competition = raw.competitions.find((c) => c.code === competitionId);
    const startDate = competition?.currentSeason?.startDate;
    const year = startDate ? new Date(startDate).getFullYear() : null;
    cacheSet(cacheKey, year, CACHE_TTL.competitions);
    return year;
  }

  /**
   * TODAS as rodadas reais da temporada (Priority 4/5 do escopo "bolão
   * funcional de verdade") — nunca uma janela artificial fixa. Busca todas
   * as partidas da temporada de uma vez (1 chamada) e deriva o conjunto de
   * rodadas a partir de (fase, `matchday`) de cada partida real — nunca só
   * `matchday` sozinho, porque esse número reinicia a cada fase nova numa
   * competição com fases (senão "rodada 1" da fase de grupos e "rodada 1"
   * dos playoffs apareceriam como uma única opção na lista, escondendo uma
   * das duas). Ordenado cronologicamente pela partida mais antiga de cada
   * grupo — não por número, já que o número sozinho não reflete a ordem
   * real quando há mais de uma fase. Cache longo (a estrutura de rodadas
   * de uma temporada não muda depois de definida).
   */
  async getSeasonRounds(competitionId: string, season: number): Promise<Array<{ round: number; label: string; stage: string | null }>> {
    const cacheKey = `rounds:${competitionId}:${season}`;
    const cached = cacheGet<Array<{ round: number; label: string; stage: string | null }>>(cacheKey);
    if (cached) return cached;

    const raw = await footballApiGet<RawMatchesResponse>(`/competitions/${competitionId}/matches`, { season });
    const groups = new Map<string, { round: number; stage: string | null; earliestTime: number }>();
    for (const m of raw.matches) {
      if (typeof m.matchday !== "number") continue;
      const stage = m.stage ?? null;
      const key = `${stage ?? "none"}:${m.matchday}`;
      const time = new Date(m.utcDate).getTime();
      const existing = groups.get(key);
      if (!existing || time < existing.earliestTime) {
        groups.set(key, { round: m.matchday, stage, earliestTime: time });
      }
    }

    const rounds = Array.from(groups.values())
      .sort((a, b) => a.earliestTime - b.earliestTime)
      .map(({ round, stage }) => {
        const stageName = stageLabel(stage);
        return { round, stage, label: stageName ? `${stageName} - Rodada ${round}` : `Rodada ${round}` };
      });

    cacheSet(cacheKey, rounds, 24 * 60 * 60 * 1000);
    return rounds;
  }
}

export const footballDataService = new FootballDataService();
